import { useServices } from "contexts/ServiceContext";
import React, { ChangeEvent, useCallback } from "react";
import useTranslation from "translations/useTranslation";
import Layout from "../Layout";
import banner from "../img/banner.jpg";
import { useShouldShowBanner } from "hooks/useShouldShowBanner";
import { HeadNormal, TextMedium } from "@tiendeo/react-components/dist/Typography";
import { useState } from "react";
import {
    Alert,
    DeleteAlertsMutation,
    DeleteAlertsMutationVariables,
    GetNotificationsByHashQuery,
    NewsletterUpdateMutation,
    NewsletterUpdateMutationVariables,
} from "graphql/schema-types";
import useGQLOperation from "hooks/useGQLOperation";
import DeleteAlerts from "services/gql/mutations/deleteAlerts";
import DeleteNotificationsModal from "./DeleteNotificationsModal/DeleteNotificationsModal";
import { EventEnum } from "enums/EventEnum";
import { Analytics } from "services/Analytics/Analytics";
import { ConfirmationModal } from "./ConfirmationModal";
import NewsletterUpdate from "services/gql/mutations/newsletterUpdate";
import { getPlatform } from "@tiendeo/js-stats-client";
import { ProfilePageTypes } from "enums/ProfilePageTypes";
import Button, { Variants } from "ui/components/Button/Button";
import {
    BannerContent,
    ButtonWrapper,
    DataManagement,
    DataManagementButton,
    DataManagementHead,
    FullPageWrapper,
    InfoWrapper,
    MoreNotifications,
    MoreNotificationsAndManagement,
    MoreNotificationsButton,
    MoreNotificationsHead,
    NewsletterCheck,
    NewsletterInput,
    Notification,
    NotificationInput,
    NotificationsList,
    NotificationsMessage,
    SelectAll,
} from "./NotificationsManagementContainer.styles";

interface ISelectedAlerts {
    [key: number]: boolean;
}

interface IModal {
    isOpen: boolean;
    isError: boolean;
    isManagement?: boolean;
    isNewsletter?: boolean;
}

const NotificationsManagementContainer = ({
    queryResult,
    hash,
}: {
    queryResult: GetNotificationsByHashQuery;
    hash: string;
}) => {
    const t = useTranslation();
    const shouldShowBanner = useShouldShowBanner();
    const services = useServices();
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAlerts, setSelectedAlerts] = useState<ISelectedAlerts>({});
    const [newsletter, setNewsletter] = useState(queryResult?.Alerts?.newsletter);
    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<{
        isOpen: boolean;
    } | null>(null);
    const [modalConfirmationState, setModalConfirmationState] = useState<IModal>({
        isOpen: false,
        isError: false,
    });
    const { alerts } = queryResult?.Alerts;
    const isSomeAlertsSelected = Object.keys(selectedAlerts).some((alertId) => {
        const value = selectedAlerts[alertId];
        return !!value;
    });
    const isDeleteButtonDisabled = !isSomeAlertsSelected || isLoading;

    const isAllSelected = findAlertsById(selectedAlerts, alerts).length === alerts.length;

    const [deleteAlertsMutation] = useGQLOperation<
        DeleteAlertsMutation,
        DeleteAlertsMutationVariables
    >(DeleteAlerts);

    const [newsletterUpdateMutation] = useGQLOperation<
        NewsletterUpdateMutation,
        NewsletterUpdateMutationVariables
    >(NewsletterUpdate);

    const goToNotifications = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.Notifications);
        services.NavigationService.Notifications();
    }, [services.NavigationService]);

    const goToHelp = useCallback(() => {
        Analytics.Instance.sendEvent(EventEnum.CategoryHelp);
        services.NavigationService.Help();
    }, [services.NavigationService]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>, id: number) => {
        setSelectedAlerts((selectedAlerts) => ({
            ...selectedAlerts,
            ...{ [id]: event.target.checked },
        }));
    };

    function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
        let newState = {};
        alerts.forEach((alert) => {
            newState = {
                ...newState,
                ...{ [alert.id]: e.target.checked },
            };
        });
        setSelectedAlerts(newState);
    }

    function findAlertsById(selectedAlerts: ISelectedAlerts, alerts: Alert[]) {
        const alertIdsToDelete = Object.keys(selectedAlerts).filter((alertId) => {
            const value = selectedAlerts[alertId];
            return !!value;
        });
        return alerts
            .filter((alert) => alertIdsToDelete.includes(alert.id.toString()))
            .map((alert) => ({
                city: alert.city || "",
                hash: hash,
                search: alert.search || "",
                id: alert.id,
            }));
    }

    async function handleDelete(selectedReason: number) {
        const alertsToDelete = findAlertsById(selectedAlerts, alerts);
        const searchList = alertsToDelete.map((alert) => {
            return alert.search;
        });
        const deleteObject = {
            input: alertsToDelete,
            reason: selectedReason,
        };

        try {
            const eventPayload = {
                Action: "Unfollow",
                ActionText: `[${searchList.toString()}]`,
                SearchType: "PROFILE",
                SearchWord: "profile",
                SearchCity: "no_city",
                View: "Notifications",
                PageType: ProfilePageTypes.ALERT_MANAGER,
            };
            await services.TiendeoStatsClient.Event(
                "Page/Action",
                services.AuthApiClient.GetUserId(),
                "",
                getPlatform(window.navigator.userAgent),
                eventPayload,
            );
            setIsLoading(true);
            await deleteAlertsMutation(deleteObject);
            setModalConfirmationState((modalConfirmationState) => ({
                ...modalConfirmationState,
                isOpen: true,
                isManagement: true,
            }));
            document.location.reload();
        } catch (error) {
            services.Logger.error(error);
            setModalConfirmationState((modalConfirmationState) => ({
                ...modalConfirmationState,
                isOpen: true,
                isError: true,
            }));
        } finally {
            setIsLoading(false);
        }
    }

    async function handleNewsletter(newsletter: boolean, hash: string, noAlerts?: boolean) {
        const newsletterVariables = {
            activate: newsletter,
            hash: hash,
        };
        try {
            noAlerts && setIsLoading(true);
            await newsletterUpdateMutation(newsletterVariables);
            await sendNewsletterStats(newsletter);
            noAlerts &&
                setModalConfirmationState((modalConfirmationState) => ({
                    ...modalConfirmationState,
                    isOpen: true,
                    isNewsletter: true,
                }));
            noAlerts && document.location.reload();
        } catch (error) {
            services.Logger.error(error);
            setModalConfirmationState((modalConfirmationState) => ({
                ...modalConfirmationState,
                isOpen: true,
                isError: true,
            }));
        } finally {
            noAlerts && setIsLoading(false);
        }
    }

    async function sendNewsletterStats(newsletter) {
        const eventPayload = {
            Action: newsletter ? "Follow" : "Unfollow",
            ActionText: "Newsletter",
            SearchType: "PROFILE",
            SearchWord: "profile",
            SearchCity: "no_city",
            View: "Follow",
            PageType: ProfilePageTypes.ALERT_MANAGER,
        };
        await services.TiendeoStatsClient.Event(
            "Page/Action",
            services.AuthApiClient.GetUserId(),
            "",
            getPlatform(window.navigator.userAgent),
            eventPayload,
        );
    }

    const renderInput = alerts?.map((notification) => {
        return (
            <Notification key={notification.id}>
                <NotificationInput
                    type="checkbox"
                    checked={selectedAlerts[notification.id]}
                    onChange={(e) => {
                        handleChange(e, notification.id);
                    }}
                />
                <TextMedium fontWeight={"bold"}>{notification.search}</TextMedium>
                <TextMedium>, {notification.city}</TextMedium>
            </Notification>
        );
    });

    return (
        <Layout showSidebar={false}>
            <DeleteNotificationsModal
                isOpen={isOpenDeleteModal?.isOpen}
                handleDelete={handleDelete}
                newsletter={newsletter}
                handleNewsletter={handleNewsletter}
                hash={hash}
                onClose={() =>
                    setIsOpenDeleteModal({
                        isOpen: false,
                    })
                }
            />
            <ConfirmationModal
                isOpen={modalConfirmationState?.isOpen}
                isError={modalConfirmationState?.isError}
                isManagement={modalConfirmationState?.isManagement}
                isNewsletter={modalConfirmationState?.isNewsletter}
                onClose={() =>
                    setModalConfirmationState((modalConfirmationState) => ({
                        ...modalConfirmationState,
                        isOpen: false,
                        isManagement: true,
                    }))
                }
            />
            <FullPageWrapper>
                <InfoWrapper>
                    <HeadNormal>{t("NOTIFICATIONS")}</HeadNormal>
                    <NotificationsMessage>
                        <TextMedium>
                            {alerts.length > 0
                                ? t("ALERTS_IN_MAIL", {
                                      0: alerts?.length,
                                      1: queryResult.Alerts?.email,
                                  })
                                : t("NO_ALERTS_IN_MAIL", { 0: queryResult.Alerts?.email })}
                        </TextMedium>
                    </NotificationsMessage>
                    <NotificationsList>
                        {alerts.length > 0 && (
                            <SelectAll>
                                <NotificationInput
                                    type="checkbox"
                                    id="SelectAll"
                                    onChange={handleSelectAll}
                                />
                                <TextMedium>{t("ANALYTICS_SELECT_ALL")}</TextMedium>
                            </SelectAll>
                        )}
                        {renderInput}
                        <NewsletterCheck>
                            <NewsletterInput
                                type="checkbox"
                                checked={newsletter}
                                onChange={() => {
                                    setNewsletter(!newsletter);
                                    sendNewsletterStats;
                                }}
                            />
                            <TextMedium>{t("SUBSCRIBE_NEWSLETTER")}</TextMedium>
                        </NewsletterCheck>
                        {alerts.length >= 1 && isSomeAlertsSelected ? (
                            <ButtonWrapper>
                                <Button
                                    variant={Variants.SOLID}
                                    color="primary"
                                    size="md"
                                    fullWidth
                                    type="button"
                                    onClick={() => {
                                        if (isAllSelected) {
                                            setIsOpenDeleteModal({
                                                isOpen: true,
                                            });
                                        } else {
                                            handleDelete(-1);
                                            handleNewsletter(newsletter, hash, false);
                                        }
                                    }}
                                    status={isDeleteButtonDisabled ? "disabled" : "active"}
                                >
                                    <TextMedium>{t("_BK_DELETE")}</TextMedium>
                                </Button>
                            </ButtonWrapper>
                        ) : (
                            <ButtonWrapper>
                                <Button
                                    variant={Variants.SOLID}
                                    color="primary"
                                    size="md"
                                    fullWidth
                                    type="button"
                                    onClick={() => {
                                        handleNewsletter(newsletter, hash, true);
                                    }}
                                    status={
                                        newsletter === queryResult.Alerts.newsletter || isLoading
                                            ? "disabled"
                                            : "active"
                                    }
                                >
                                    <TextMedium>{t("UPDATE_NEWSLETTER")}</TextMedium>
                                </Button>
                            </ButtonWrapper>
                        )}
                    </NotificationsList>
                    <MoreNotificationsAndManagement>
                        <MoreNotifications>
                            <MoreNotificationsHead>
                                {t("ADD_NOTIFICATIONS_TITLE")}
                            </MoreNotificationsHead>
                            <TextMedium>{t("CHOOSE_YOUR_FAVOURITES")}</TextMedium>
                            <MoreNotificationsButton
                                variant={Variants.SOLID}
                                color="primary"
                                size="md"
                                fullWidth
                                type="button"
                                onClick={goToNotifications}
                            >
                                <TextMedium>{t("ADD_MORE_NOTIFICATIONS")}</TextMedium>
                            </MoreNotificationsButton>
                        </MoreNotifications>
                        <DataManagement>
                            <DataManagementHead>
                                {t("ALERTS_MANAGEMENT_PRIVACY_TITLE")}
                            </DataManagementHead>
                            <TextMedium>{t("ALERTS_MANAGEMENT_PRIVACY_DESCRIPTION")}</TextMedium>
                            <DataManagementButton
                                variant={Variants.SOLID}
                                color="secondary"
                                size="md"
                                fullWidth
                                type="button"
                                onClick={goToHelp}
                            >
                                <TextMedium>{t("ALERTS_MANAGEMENT_PRIVACY_BUTTON")}</TextMedium>
                            </DataManagementButton>
                        </DataManagement>
                    </MoreNotificationsAndManagement>
                </InfoWrapper>
                {shouldShowBanner && (
                    <BannerContent>
                        <a
                            href={`${services.Configs.url_apps}?source=perfil&medium=web`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img src={banner as any} width={287} height={455} alt="app banner" />
                        </a>
                    </BannerContent>
                )}
            </FullPageWrapper>
        </Layout>
    );
};

export default NotificationsManagementContainer;
